/**
 * The notification center: the bell, the unread badge, and the dropdown of handoffs.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Work order M5 section 7: one notification center, rows {who, what, deep link, when,
 * read state}, mark-read on click plus a mark-all, polled every 60 seconds while signed
 * in (no websocket in the MVP). A deep link is an SPA route: it navigates inside the
 * shell, it never reloads the document. The rows are the server's own; an empty list is
 * an empty list, and a failed poll renders the failure rather than a fake count.
 *
 * WAVE W2 (Mahmoud's UAT, 2026-10-10: "if I act on a task from My tasks, it should leave
 * the notifications menu; incomplete stays"). A handoff that announced a task follows
 * the task: while the task waits it stays in the menu marked "Waiting on you", read or
 * not; once the task is complete it is ATTENDED, leaves the badge and folds under
 * "Done". Both states are the server's reading of the task; nothing is cleared here.
 */

import { Bell } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import {
  isAttended,
  isUnread,
  listNotifications,
  markNotificationRead,
  type Notification,
} from "../api/notifications";
import { strings } from "../brand/strings";
import { canonicalPath } from "../nav/deepLinks";
import { day } from "../screens/parts";

export const NOTIFICATION_POLL_MS = 60_000;

interface NotificationBellProps {
  readonly token: string;
}

/**
 * The bell button and its dropdown. The poll runs while the component is mounted (the
 * shell mounts it for the whole signed-in session); the interval is cleared on unmount
 * and re-armed only by the mount, so a slow answer never stacks a second timer.
 */
export function NotificationBell({ token }: NotificationBellProps): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [openedAt, setOpenedAt] = useState(location.key);
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [failed, setFailed] = useState(false);
  const busyId = useRef<string | null>(null);

  const refresh = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        const body = await listNotifications(token, { signal: signal ?? undefined });
        setItems(body.items);
        setUnread(body.unread);
        setFailed(false);
      } catch (error: unknown) {
        if (signal?.aborted || (error instanceof DOMException && error.name === "AbortError")) {
          return;
        }
        setFailed(true);
      }
    },
    [token],
  );

  useEffect(() => {
    const controller = new AbortController();
    // The first read is deferred one tick so the effect body itself never sets state.
    const first = window.setTimeout(() => {
      void refresh(controller.signal);
    }, 0);
    const timer = window.setInterval(() => {
      void refresh();
    }, NOTIFICATION_POLL_MS);
    return () => {
      controller.abort();
      window.clearTimeout(first);
      window.clearInterval(timer);
    };
  }, [refresh]);

  // Wave W2: a task acted on elsewhere (My tasks, the record page) leaves the bell on the
  // next screen, not a minute later: the bell re-reads when the page changes.
  useEffect(() => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void refresh(controller.signal);
    }, 0);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [refresh, location.pathname]);

  // Coherence review R11 (wave W0): the flyout never outlives the page it was opened on.
  // It closes on navigation, on Escape, and on a click anywhere outside it, so it can
  // never sit over the next page catching the clicks meant for it.
  if (open && openedAt !== location.key) {
    setOpen(false);
  }
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointer = (event: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function markRead(entry: Notification): Promise<void> {
    // An attended row is not unread (its task is done): nothing to mark or count.
    if (!isUnread(entry) || busyId.current === entry.id) {
      return;
    }
    busyId.current = entry.id;
    try {
      const row = await markNotificationRead(token, entry.id);
      setItems((current) =>
        current.map((item) =>
          item.id === row.id ? { ...item, readAt: row.readAt ?? new Date().toISOString() } : item,
        ),
      );
      setUnread((count) => Math.max(0, count - 1));
    } catch {
      // The row stays unread on screen; the next poll re-reads the server's truth.
    } finally {
      busyId.current = null;
    }
  }

  function openEntry(entry: Notification): void {
    setOpen(false);
    void markRead(entry);
    if (entry.deepLink !== null) {
      void navigate(canonicalPath(entry.deepLink));
    }
  }

  async function markAll(): Promise<void> {
    const pending = items.filter(isUnread);
    for (const entry of pending) {
      await markRead(entry);
    }
  }

  const active = items.filter((entry) => !isAttended(entry));
  const attended = items.filter(isAttended);

  return (
    <div className="q-bell" ref={rootRef}>
      <button
        type="button"
        className="q-button q-button--quiet q-shell__bell"
        data-testid="notification-bell"
        aria-expanded={open}
        aria-controls="q-bell-menu"
        title={strings.shellUi.notifications}
        onClick={() => {
          setOpenedAt(location.key);
          // Wave W2: opening the bell reads it afresh, so an act taken on this very page
          // (the record's own task done in place) has already left it.
          if (!open) {
            void refresh();
          }
          setOpen((value) => !value);
        }}
      >
        {/* The phone bar shows the glyph; the word stays the accessible name. */}
        <Bell className="q-shell__bar-icon" aria-hidden="true" strokeWidth={1.75} />
        <span className="q-shell__bar-label">{strings.shellUi.notifications}</span>
        {unread > 0 && (
          <span className="q-bell__badge" data-testid="notification-unread">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div className="q-bell__menu" id="q-bell-menu" data-testid="notification-menu">
          <div className="q-bell__menu-head">
            <span>{strings.shellUi.notifications}</span>
            {unread > 0 && (
              <button
                type="button"
                className="q-button q-button--quiet"
                data-testid="notification-mark-all"
                onClick={() => {
                  void markAll();
                }}
              >
                {strings.shellUi.notificationsMarkAll}
              </button>
            )}
          </div>
          {failed && (
            <p className="q-note" role="alert">
              {strings.shellUi.notificationsFailure}
            </p>
          )}
          {!failed && active.length === 0 && (
            <p className="q-note">{strings.shellUi.notificationsEmpty}</p>
          )}
          <ul className="q-bell__list">
            {active.map((entry) => (
              <li key={entry.id}>
                <button
                  type="button"
                  className={`q-bell__item${isUnread(entry) ? " q-bell__item--unread" : ""}`}
                  data-testid="notification-item"
                  data-task-open={entry.taskOpen ? "true" : undefined}
                  onClick={() => {
                    openEntry(entry);
                  }}
                >
                  <span className="q-bell__message">{entry.message}</span>
                  <span className="q-bell__meta">
                    {day(entry.createdAt)}
                    {entry.taskOpen ? ` · ${strings.shellUi.notificationsWaiting}` : ""}
                    {!entry.taskOpen && isUnread(entry)
                      ? ` · ${strings.shellUi.notificationsUnread}`
                      : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {attended.length > 0 && (
            <details className="q-bell__done" data-testid="notification-attended">
              <summary className="q-bell__done-summary">
                {strings.shellUi.notificationsAttended(attended.length)}
              </summary>
              <p className="q-note q-bell__done-lede">
                {strings.shellUi.notificationsAttendedLede}
              </p>
              <ul className="q-bell__list">
                {attended.map((entry) => (
                  <li key={entry.id}>
                    <button
                      type="button"
                      className="q-bell__item q-bell__item--attended"
                      data-testid="notification-attended-item"
                      onClick={() => {
                        openEntry(entry);
                      }}
                    >
                      <span className="q-bell__message">{entry.message}</span>
                      <span className="q-bell__meta">
                        {strings.shellUi.notificationsDoneOn(day(entry.attendedAt))}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          )}
          <div
            className="q-bell__menu-foot"
            style={{ padding: "0.5rem", borderTop: "1px solid var(--q-rule)", textAlign: "center" }}
          >
            <button
              type="button"
              className="q-button q-button--quiet"
              data-testid="notification-view-all"
              onClick={() => {
                setOpen(false);
                void navigate("/tasks");
              }}
            >
              {strings.shellUi.notificationsViewAll}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
